import React, { useState, useRef, useEffect } from 'react';
import './AutocompleteInput.scss';

interface AutocompleteOption {
  id: number;
  value: string;
}

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  label?: string;
  allowCustomValue?: boolean;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  options,
  placeholder,
  label,
  allowCustomValue = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    
    if (inputValue.trim()) {
      const filtered = options.filter(option =>
        option.value.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredOptions([]);
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (option: AutocompleteOption) => {
    onChange(option.value);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleInputFocus = () => {
    if (value.trim()) {
      const filtered = options.filter(option =>
        option.value.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredOptions(options.slice(0, 10)); // Show first 10 options
      setIsOpen(options.length > 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <div className="autocomplete-container" ref={containerRef}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="autocomplete-input"
        />
        
        {isOpen && filteredOptions.length > 0 && (
          <div className="autocomplete-dropdown">
            {filteredOptions.map((option) => (
              <div
                key={option.id}
                className="autocomplete-option"
                onClick={() => handleOptionSelect(option)}
              >
                {option.value}
              </div>
            ))}
            {allowCustomValue && value && !filteredOptions.some(opt => opt.value.toLowerCase() === value.toLowerCase()) && (
              <div className="autocomplete-custom">
                <em>Naudoti: "{value}"</em>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutocompleteInput;
